import { Spacing } from '@persona-id/design-tokens';
import { some } from 'lodash/fp';
import { useMemo, VFC } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import CodeEditor from 'dashboard/components-lib/CodeEditor';
import casesCompletionSource from 'dashboard/components-lib/CodeEditor/completionSources/javascript/casesCompletionSource';
import { CaseQuery } from 'dashboard/models/case-query';
import { CaseDataManagerSchema } from 'dashboard/models/case-template';
import {
  EXTERNAL_INTEGRATION_DISPLAY_NAME,
  ExternalIntegrationDataSource,
} from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const QueryOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;

const EditorContainer = styled.div`
  height: 300px;
`;

export interface FormEditCaseQueryProps {
  caseQuery: CaseQuery;
  allCaseQueries: CaseQuery[];
  caseDataManagerSchema: CaseDataManagerSchema;
  dataSources: ExternalIntegrationDataSource[];
  submit: (data: FormEditCaseQueryData) => void;
  handleClose: () => void;
}

const FormEditCaseQuery: VFC<FormEditCaseQueryProps> = ({
  caseQuery,
  allCaseQueries,
  caseDataManagerSchema,
  dataSources,
  submit,
  handleClose,
}) => {
  const {
    control,
    register: wrappedRegister,
    getValues,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors },
  } = useFormSecurely({
    defaultValues: {
      queryName: caseQuery.attributes.name,
      queryBody: caseQuery.attributes.body,
      querySource:
        caseQuery.attributes['query-type'] === 'javascript'
          ? 'javascript'
          : `datasource-${caseQuery.relationships['external-integration']?.data?.id}`,
    },
    mode: 'onChange',
  });

  const existingQueryNames = useMemo(() => {
    return allCaseQueries
      .filter((query) => query.id !== caseQuery.id)
      .map((query) => query.attributes.name);
  }, [allCaseQueries, caseQuery]);

  const dataSourceSchema = (selectedDataSourceId: string) => {
    if (selectedDataSourceId == null) {
      return [];
    }

    const selectedDataSource = dataSources.find((source) => source.id === selectedDataSourceId);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return selectedDataSource.attributes.schema.tables;
  };

  return (
    <form
      onSubmit={(e) => {
        // We have to prevent submission of the parent settings form
        e.preventDefault();
        handleSubmit(submit)();
        e.stopPropagation();
      }}
    >
      <Label size='md' htmlFor='form-edit-case-query__query-name' weight='bold'>
        Name
      </Label>
      <Input
        {...wrappedRegister('queryName', {
          required: 'Please give your query a name.',
          pattern: {
            value: /^[a-zA-Z0-9]+$/,
            message: 'Please only enter alphanumeric characters for query name.',
          },
          validate: (newQueryName) => {
            return some((name) => name === newQueryName, existingQueryNames)
              ? 'Query name already exists. Please choose a different name.'
              : true;
          },
        })}
        id='form-edit-case-query__query-name'
        type='text'
        autoFocus={true}
        disabled={isSubmitting}
      />
      {errors.queryName && <FormError role='alert'>{errors.queryName.message}</FormError>}
      <Spacer height={2} />
      <Label size='md' htmlFor='form-add-case-field__field-key' weight='bold'>
        Data source
      </Label>
      <Controller
        name='querySource'
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Select {...field}>
            {dataSources.map((source) => (
              <Option key={source.id} value={`datasource-${source.id}`}>
                <QueryOptionContainer>
                  {source.attributes.name}
                  <Sticker>{EXTERNAL_INTEGRATION_DISPLAY_NAME[source.type]}</Sticker>
                </QueryOptionContainer>
              </Option>
            ))}
            <Option key='javascript' value='javascript'>
              <QueryOptionContainer>Run JS Query</QueryOptionContainer>
            </Option>
          </Select>
        )}
      />
      {errors.querySource && <FormError role='alert'>{errors.querySource.message}</FormError>}
      <Spacer height={2} />
      <Label size='md' htmlFor='form-edit-case-query__query-body' weight='bold'>
        Body
      </Label>
      <Controller
        control={control}
        name='queryBody'
        rules={{ required: 'Please enter a query body.' }}
        render={({ field: { onChange, onBlur, value } }) => (
          <EditorContainer>
            {getValues('querySource') === 'javascript' ? (
              <CodeEditor
                language='javascript'
                code={value}
                hasError={!!errors.queryBody}
                readOnly={isSubmitting}
                completionSources={{
                  javascript: [
                    casesCompletionSource({
                      caseDataManager: caseDataManagerSchema,
                    }),
                  ],
                }}
                onChange={onChange}
                onBlur={onBlur}
              />
            ) : (
              <CodeEditor
                language='sqljs'
                code={value}
                hasError={!!errors.queryBody}
                readOnly={isSubmitting}
                schemas={{
                  sql: {
                    tables: dataSourceSchema(getValues('querySource')?.split('-')?.[1]),
                  },
                }}
                completionSources={{
                  javascript: [
                    casesCompletionSource({
                      caseDataManager: caseDataManagerSchema,
                    }),
                  ],
                }}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          </EditorContainer>
        )}
      />
      {errors.queryBody && <FormError role='alert'>{errors.queryBody.message}</FormError>}
      <Spacer height={4} />
      <HStack equal>
        <Button
          id='form-edit-case-query__cancel'
          variant='basic'
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          id='form-edit-case-query__confirm'
          disabled={!isDirty || isSubmitting}
          type='submit'
          value='Submit'
        >
          Save
        </Button>
      </HStack>
    </form>
  );
};

export interface FormEditCaseQueryData {
  querySource: string;
  queryName: string;
  queryBody: string;
}

export default FormEditCaseQuery;
